import { useCallback } from 'react'

import { useFetchRefactored, Status } from 'civic-champs-shared/api/hooks'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'
import { Organization } from 'organization/types'
import { OpportunityStatus } from 'Event/interfaces/interfaceCreateEditEvent'

export interface RawEventsQuery {
  from: Date | string
  to: Date | string
}

export interface RawEventRole {
  id: number
  name: string
  available: number
}

export interface RawEventShift {
  id: number
  name: string
  timeStart: string
  timeEnd: string
  roles: RawEventRole[]
}

export interface RawEvent {
  occurrenceId: number
  opportunityId: number
  status: OpportunityStatus
  name: string
  description: string
  shiftStart: string
  shiftEnd: string
  roles: RawEventRole[]
  // Absent on API versions that only return the aggregated shift window
  shifts?: RawEventShift[]
  recurrencePattern: string
}

export interface RawEventRegistrations {
  opportunityId: number
  dates: string[]
  roleIds: number[]
  timeshiftIds?: number[]
  count: number[]
}

export interface RawEventResponse {
  organization: Partial<Organization>
  query: RawEventsQuery
  events: RawEvent[]
  registrations: RawEventRegistrations[]
}

export const useGetRawEvents = (): [(query: RawEventsQuery) => Promise<RawEventResponse>, Status<RawEventResponse>] => {
  const organization = useCurrentOrg()
  const [request, status] = useFetchRefactored<RawEventResponse>()

  const getRawEvents = useCallback(
    async (query: RawEventsQuery) =>
      request({
        url: `/organizations/${organization.id}/raw_events`,
        config: {
          queryStringParameters: query,
        },
      }),
    [request, organization.id],
  )

  return [getRawEvents, status]
}
